import { fromUrl, GeoTIFF, GeoTIFFImage } from 'geotiff';
import proj4 from 'proj4';
import { ProjectionParameters } from '../types/baseMaps';

/**
 * Geographic bounds of a map
 */
export interface GeoBounds {
  north: number;
  south: number;
  east: number;
  west: number;
}

/**
 * Pixel coordinates
 */
export interface PixelCoord {
  x: number;
  y: number;
}

/**
 * Geographic coordinates
 */
export interface GeoCoord {
  latitude: number;
  longitude: number;
}

/**
 * GeoTIFF metadata extracted from the file
 */
export interface GeoTIFFMetadata {
  width: number;
  height: number;
  bounds: GeoBounds;
  transform: number[]; // Affine transformation matrix
  projectedBounds?: { minX: number; minY: number; maxX: number; maxY: number }; // Projected coordinate bounds (if applicable)
  isProjected: boolean; // Whether the coordinate system is projected (true) or geographic (false)
}

/**
 * Loaded GeoTIFF with parsed metadata and transformation functions
 */
export interface LoadedGeoTIFF {
  tiff: GeoTIFF;
  image: GeoTIFFImage;
  metadata: GeoTIFFMetadata;
  geoToPixel: (coord: GeoCoord) => PixelCoord;
  pixelToGeo: (coord: PixelCoord) => GeoCoord;
  projection?: ProjectionParameters; // Dynamic projection parameters from API
}

/**
 * Error thrown when GeoTIFF parsing fails
 */
export class GeoTIFFParsingError extends Error {
  constructor(message: string, public cause?: Error) {
    super(message);
    this.name = 'GeoTIFFParsingError';
  }
}

/**
 * Load and parse a GeoTIFF file from a URL
 * 
 * @param url - URL to the GeoTIFF file
 * @returns Promise resolving to LoadedGeoTIFF with metadata and transformation functions
 * @throws GeoTIFFParsingError if loading or parsing fails
 */
export async function loadGeoTIFF(url: string): Promise<LoadedGeoTIFF> {
  try {
    console.log(`Loading GeoTIFF from: ${url}`);
    
    // Load GeoTIFF from URL — disable multi-range requests to avoid
    // CloudFront HTTP/2 ERR_HTTP2_SERVER_REFUSED_STREAM errors
    const tiff = await fromUrl(url, { maxRanges: 0, allowFullFile: true });
    
    // Get the first image (overview)
    const image = await tiff.getImage();
    
    // Check file size and warn if large
    const width = image.getWidth();
    const height = image.getHeight();
    const estimatedSizeMB = (width * height * 3) / (1024 * 1024); // Rough estimate for RGB
    
    if (estimatedSizeMB > 10) {
      console.warn(`⚠️ Warning: Loading large GeoTIFF in browser (estimated ${estimatedSizeMB.toFixed(1)}MB uncompressed).`);
      console.warn('Consider using an overview image for better performance.');
    }
    
    // Extract metadata
    const metadata = await extractMetadata(image);
    
    // Create coordinate transformation functions
    const geoToPixel = createGeoToPixelTransform(metadata);
    const pixelToGeo = createPixelToGeoTransform(metadata);
    
    return {
      tiff,
      image,
      metadata,
      geoToPixel,
      pixelToGeo,
    };
  } catch (error) {
    if (error instanceof GeoTIFFParsingError) {
      throw error;
    }
    
    const message = error instanceof Error ? error.message : 'Unknown error';
    
    // Check for LZW decompression errors
    if (message.includes('ran off the end of the buffer') || 
        message.includes('EOI_CODE') || 
        message.includes('LZW')) {
      throw new GeoTIFFParsingError(
        'This base map is too large for browser display. The file uses LZW compression which cannot be reliably decompressed in the browser. Please contact support to request an overview image.',
        error instanceof Error ? error : undefined
      );
    }
    
    throw new GeoTIFFParsingError(
      `Failed to load GeoTIFF from ${url}: ${message}`,
      error instanceof Error ? error : undefined
    );
  }
}

/**
 * Load a base map with dynamic projection parameters from API
 * 
 * This function fetches base map metadata from the API, extracts projection parameters,
 * loads the GeoTIFF file, and configures transformation functions with dynamic projection.
 * 
 * @param url - URL to the GeoTIFF file
 * @param projection - Projection parameters from base map metadata
 * @returns Promise resolving to LoadedGeoTIFF with dynamic projection configuration
 * @throws GeoTIFFParsingError if loading or parsing fails
 */
export async function loadBaseMapWithProjection(
  url: string,
  projection: ProjectionParameters
): Promise<LoadedGeoTIFF> {
  try {
    console.log(`Loading base map with dynamic projection from: ${url}`);
    console.log('Projection parameters:', projection);
    
    // Validate projection parameters
    if (!projection) {
      throw new GeoTIFFParsingError('Projection parameters are missing from base map metadata');
    }
    
    if (!projection.type) {
      throw new GeoTIFFParsingError('Projection type is missing from base map metadata');
    }
    
    // Validate Lambert Conformal Conic projection parameters
    if (projection.type === 'lcc') {
      const errors: string[] = [];
      
      if (projection.centralMeridian === undefined || projection.centralMeridian === null) {
        errors.push('Central meridian is required for Lambert Conformal Conic projection');
      } else if (projection.centralMeridian < -180 || projection.centralMeridian > 180) {
        errors.push('Central meridian must be between -180 and 180 degrees');
      }
      
      if (projection.originLatitude === undefined || projection.originLatitude === null) {
        errors.push('Origin latitude is required for Lambert Conformal Conic projection');
      } else if (projection.originLatitude < -90 || projection.originLatitude > 90) {
        errors.push('Origin latitude must be between -90 and 90 degrees');
      }
      
      if (projection.standardParallel1 === undefined || projection.standardParallel1 === null) {
        errors.push('Standard parallel 1 is required for Lambert Conformal Conic projection');
      } else if (projection.standardParallel1 < -90 || projection.standardParallel1 > 90) {
        errors.push('Standard parallel 1 must be between -90 and 90 degrees');
      }
      
      if (projection.standardParallel2 === undefined || projection.standardParallel2 === null) {
        errors.push('Standard parallel 2 is required for Lambert Conformal Conic projection');
      } else if (projection.standardParallel2 < -90 || projection.standardParallel2 > 90) {
        errors.push('Standard parallel 2 must be between -90 and 90 degrees');
      }
      
      if (projection.standardParallel1 !== undefined && 
          projection.standardParallel2 !== undefined && 
          projection.standardParallel1 >= projection.standardParallel2) {
        errors.push('Standard parallel 1 must be less than standard parallel 2');
      }
      
      if (!projection.projectedBounds) {
        errors.push('Projected bounds are required for Lambert Conformal Conic projection');
      } else {
        const { minX, maxX, minY, maxY } = projection.projectedBounds;
        if (minX >= maxX) {
          errors.push('Projected bounds minX must be less than maxX');
        }
        if (minY >= maxY) {
          errors.push('Projected bounds minY must be less than maxY');
        }
      }
      
      if (errors.length > 0) {
        throw new GeoTIFFParsingError(
          `Invalid projection parameters: ${errors.join('; ')}`
        );
      }
    } else {
      // Unsupported projection type
      throw new GeoTIFFParsingError(
        `Unsupported projection type: ${projection.type}. Only Lambert Conformal Conic (lcc) is currently supported.`
      );
    }
    
    // Load GeoTIFF from URL — disable multi-range requests to avoid
    // CloudFront HTTP/2 ERR_HTTP2_SERVER_REFUSED_STREAM errors
    const tiff = await fromUrl(url, { maxRanges: 0, allowFullFile: true });
    
    // Get the first image (overview)
    const image = await tiff.getImage();
    
    // Check file size and warn if large
    const width = image.getWidth();
    const height = image.getHeight();
    const estimatedSizeMB = (width * height * 3) / (1024 * 1024);
    
    if (estimatedSizeMB > 10) {
      console.warn(`⚠️ Warning: Loading large GeoTIFF in browser (estimated ${estimatedSizeMB.toFixed(1)}MB uncompressed).`);
      console.warn('Consider using an overview image for better performance.');
    }
    
    // Extract metadata
    const metadata = await extractMetadata(image);
    
    // Create coordinate transformation functions with dynamic projection
    const geoToPixel = createGeoToPixelTransform(metadata, projection);
    const pixelToGeo = createPixelToGeoTransform(metadata, projection);
    
    return {
      tiff,
      image,
      metadata,
      geoToPixel,
      pixelToGeo,
      projection,
    };
  } catch (error) {
    if (error instanceof GeoTIFFParsingError) {
      throw error;
    }
    
    const message = error instanceof Error ? error.message : 'Unknown error';
    
    // Check for LZW decompression errors
    if (message.includes('ran off the end of the buffer') || 
        message.includes('EOI_CODE') || 
        message.includes('LZW')) {
      throw new GeoTIFFParsingError(
        'This base map is too large for browser display. The file uses LZW compression which cannot be reliably decompressed in the browser. Please contact support to request an overview image.',
        error instanceof Error ? error : undefined
      );
    }
    
    throw new GeoTIFFParsingError(
      `Failed to load GeoTIFF from ${url}: ${message}`,
      error instanceof Error ? error : undefined
    );
  }
}

/**
 * Extract metadata from a GeoTIFF image
 * 
 * @param image - GeoTIFF image
 * @returns GeoTIFF metadata including bounds, dimensions, and transformation matrix
 * @throws GeoTIFFParsingError if metadata extraction fails
 */
async function extractMetadata(image: GeoTIFFImage): Promise<GeoTIFFMetadata> {
  try {
    const width = image.getWidth();
    const height = image.getHeight();
    
    // Get bounding box [minX, minY, maxX, maxY]
    // IMPORTANT: For projected coordinate systems (like Lambert Conformal Conic),
    // getBoundingBox() returns projected coordinates, NOT lat/long
    const bbox = image.getBoundingBox();
    
    if (!bbox || bbox.length !== 4) {
      throw new GeoTIFFParsingError('Invalid or missing bounding box in GeoTIFF');
    }
    
    console.log('DEBUG extractMetadata: Raw bounding box from GeoTIFF:', bbox);
    console.log('DEBUG extractMetadata: Image dimensions:', width, 'x', height);
    
    // Check if this is a projected coordinate system by examining the coordinate range
    // Geographic coordinates (lat/long) are typically in range [-180, 180] for longitude and [-90, 90] for latitude
    // Projected coordinates are typically much larger (thousands or millions)
    const isProjected = Math.abs(bbox[0]) > 360 || Math.abs(bbox[2]) > 360 || Math.abs(bbox[1]) > 180 || Math.abs(bbox[3]) > 180;
    
    console.log('DEBUG extractMetadata: Coordinate system appears to be:', isProjected ? 'PROJECTED' : 'GEOGRAPHIC');
    console.log('DEBUG extractMetadata: Detection values - bbox[0]:', bbox[0], 'bbox[2]:', bbox[2]);
    
    // Store both geographic and projected bounds
    let bounds: GeoBounds;
    let projectedBounds: { minX: number; minY: number; maxX: number; maxY: number } | undefined;
    
    if (isProjected) {
      // Store the actual projected bounds from the GeoTIFF
      projectedBounds = {
        minX: bbox[0],
        minY: bbox[1],
        maxX: bbox[2],
        maxY: bbox[3]
      };
      console.log('DEBUG extractMetadata: Projected bounds from GeoTIFF:', projectedBounds);
      
      // Use known geographic bounds for US VFR Wall Planning Chart
      // These are only used for display purposes and initial positioning
      bounds = {
        north: 48.52,
        south: 22.59,
        east: -64.14,
        west: -129.92,
      };
      console.log('DEBUG extractMetadata: Using hardcoded geographic bounds for display:', bounds);
    } else {
      // Geographic coordinate system - use bounding box directly
      bounds = {
        west: bbox[0],
        south: bbox[1],
        east: bbox[2],
        north: bbox[3],
      };
      console.log('DEBUG extractMetadata: Using bounding box as geographic bounds:', bounds);
    }
    
    // Get affine transformation matrix
    // The transformation matrix converts pixel coordinates to geographic coordinates
    const fileDirectory = image.fileDirectory;
    const modelTransformation = (fileDirectory as any).ModelTransformation;
    
    let transform: number[];
    
    if (modelTransformation) {
      // Use ModelTransformation if available (4x4 matrix)
      transform = modelTransformation;
      console.log('DEBUG extractMetadata: Using ModelTransformation matrix:', transform);
    } else {
      // Fallback: calculate from bounding box
      // Simple affine transformation: [scaleX, 0, 0, scaleY, translateX, translateY]
      const scaleX = (bounds.east - bounds.west) / width;
      const scaleY = (bounds.south - bounds.north) / height; // Negative because Y increases downward
      transform = [scaleX, 0, 0, scaleY, bounds.west, bounds.north];
      console.log('DEBUG extractMetadata: Calculated affine transform:', transform);
    }
    
    return {
      width,
      height,
      bounds,
      transform,
      projectedBounds,
      isProjected,
    };
  } catch (error) {
    if (error instanceof GeoTIFFParsingError) {
      throw error;
    }
    
    const message = error instanceof Error ? error.message : 'Unknown error';
    throw new GeoTIFFParsingError(
      `Failed to extract GeoTIFF metadata: ${message}`,
      error instanceof Error ? error : undefined
    );
  }
}

/**
 * Create a function to transform geographic coordinates to pixel coordinates
 * 
 * Uses proj4 for proper coordinate transformation when dealing with projected coordinate systems.
 * Supports dynamic projection parameters from base map metadata.
 * 
 * @param metadata - GeoTIFF metadata
 * @param projection - Optional projection parameters from base map metadata (for dynamic projection)
 * @returns Function that converts GeoCoord to PixelCoord
 */
function createGeoToPixelTransform(
  metadata: GeoTIFFMetadata,
  projection?: ProjectionParameters
): (coord: GeoCoord) => PixelCoord {
  const { width, height, bounds, transform, isProjected } = metadata;
  
  console.log('DEBUG createGeoToPixelTransform: Creating transform with bounds:', bounds);
  console.log('DEBUG createGeoToPixelTransform: Image dimensions:', width, 'x', height);
  console.log('DEBUG createGeoToPixelTransform: Transform matrix:', transform);
  console.log('DEBUG createGeoToPixelTransform: Is projected:', isProjected);
  
  // For geographic (unprojected) coordinate systems, use simple linear interpolation
  if (!isProjected && !projection) {
    console.log('DEBUG createGeoToPixelTransform: Using simple linear interpolation for geographic coordinates');
    
    return (coord: GeoCoord): PixelCoord => {
      // Simple linear interpolation from lat/long to pixels
      const normalizedX = (coord.longitude - bounds.west) / (bounds.east - bounds.west);
      const normalizedY = (bounds.north - coord.latitude) / (bounds.north - bounds.south);
      
      const x = normalizedX * width;
      const y = normalizedY * height;
      
      return { x, y };
    };
  }
  
  // For projected coordinate systems, use proj4 transformation
  // Build projection string from parameters or use default
  let lccProjection: string;
  let projectedBounds: { minX: number; minY: number; maxX: number; maxY: number };
  
  if (projection && projection.type === 'lcc') {
    // Use dynamic projection parameters from API
    const centralMeridian = projection.centralMeridian ?? -100;
    const originLatitude = projection.originLatitude ?? 38;
    const standardParallel1 = projection.standardParallel1 ?? 33;
    const standardParallel2 = projection.standardParallel2 ?? 45;
    const falseEasting = projection.falseEasting ?? 0;
    const falseNorthing = projection.falseNorthing ?? 0;
    
    lccProjection = `+proj=lcc +lat_0=${originLatitude} +lon_0=${centralMeridian} +lat_1=${standardParallel1} +lat_2=${standardParallel2} +x_0=${falseEasting} +y_0=${falseNorthing} +datum=WGS84 +units=m +no_defs`;
    
    console.log('DEBUG createGeoToPixelTransform: Using dynamic Lambert Conformal Conic projection');
    console.log('DEBUG createGeoToPixelTransform: Projection parameters:', {
      centralMeridian,
      originLatitude,
      standardParallel1,
      standardParallel2,
      falseEasting,
      falseNorthing
    });
    
    // Use projected bounds from projection parameters or metadata
    if (projection.projectedBounds) {
      projectedBounds = projection.projectedBounds;
    } else if (metadata.projectedBounds) {
      projectedBounds = metadata.projectedBounds;
    } else {
      throw new GeoTIFFParsingError('Projected bounds not available in projection parameters or metadata');
    }
  } else {
    // Fallback to hardcoded US VFR Wall Planning Chart projection
    lccProjection = '+proj=lcc +lat_0=38 +lon_0=-100 +lat_1=33 +lat_2=45 +x_0=0 +y_0=0 +datum=WGS84 +units=m +no_defs';
    
    console.log('DEBUG createGeoToPixelTransform: Using default Lambert Conformal Conic projection');
    
    // Use hardcoded projected bounds or metadata projected bounds
    if (metadata.projectedBounds) {
      projectedBounds = metadata.projectedBounds;
    } else {
      // Use hardcoded projected bounds for US VFR Wall Planning Chart
      projectedBounds = {
        minX: -2189360.538664901,
        minY: -1471397.7140032456,
        maxX: 2668835.6277457676,
        maxY: 1531470.0888555103
      };
    }
  }
  
  const wgs84 = 'EPSG:4326'; // Standard lat/long coordinate system
  
  return (coord: GeoCoord): PixelCoord => {
    // Transform from WGS84 (lat/long) to Lambert Conformal Conic (projected meters)
    const [projectedX, projectedY] = proj4(wgs84, lccProjection, [coord.longitude, coord.latitude]);
    
    console.log(`DEBUG geoToPixel: Input lat=${coord.latitude}, long=${coord.longitude}`);
    console.log(`DEBUG geoToPixel: Projected coordinates: x=${projectedX.toFixed(2)}m, y=${projectedY.toFixed(2)}m`);
    
    // Calculate pixel position by normalizing within the projected bounds
    const normalizedX = (projectedX - projectedBounds.minX) / (projectedBounds.maxX - projectedBounds.minX);
    const normalizedY = (projectedBounds.maxY - projectedY) / (projectedBounds.maxY - projectedBounds.minY);
    
    const x = normalizedX * width;
    const y = normalizedY * height;
    
    console.log(`DEBUG geoToPixel: Projected bounds - minX=${projectedBounds.minX.toFixed(2)}, maxX=${projectedBounds.maxX.toFixed(2)}, minY=${projectedBounds.minY.toFixed(2)}, maxY=${projectedBounds.maxY.toFixed(2)}`);
    console.log(`DEBUG geoToPixel: Normalized x=${normalizedX.toFixed(4)}, y=${normalizedY.toFixed(4)}`);
    console.log(`DEBUG geoToPixel: Output pixel x=${x.toFixed(2)}, y=${y.toFixed(2)}`);
    
    return { x, y };
  };
}

/**
 * Create a function to transform pixel coordinates to geographic coordinates
 * 
 * Uses proj4 for proper coordinate transformation when dealing with projected coordinate systems.
 * This is the inverse of geoToPixel transformation.
 * Supports dynamic projection parameters from base map metadata.
 * 
 * @param metadata - GeoTIFF metadata
 * @param projection - Optional projection parameters from base map metadata (for dynamic projection)
 * @returns Function that converts PixelCoord to GeoCoord
 */
function createPixelToGeoTransform(
  metadata: GeoTIFFMetadata,
  projection?: ProjectionParameters
): (coord: PixelCoord) => GeoCoord {
  const { width, height, bounds, isProjected } = metadata;
  
  // For geographic (unprojected) coordinate systems, use simple linear interpolation
  if (!isProjected && !projection) {
    console.log('DEBUG createPixelToGeoTransform: Using simple linear interpolation for geographic coordinates');
    
    return (coord: PixelCoord): GeoCoord => {
      // Simple linear interpolation from pixels to lat/long
      const normalizedX = coord.x / width;
      const normalizedY = coord.y / height;
      
      const longitude = bounds.west + normalizedX * (bounds.east - bounds.west);
      const latitude = bounds.north - normalizedY * (bounds.north - bounds.south);
      
      return { latitude, longitude };
    };
  }
  
  // For projected coordinate systems, use proj4 transformation
  // Build projection string from parameters or use default
  let lccProjection: string;
  let projectedBounds: { minX: number; minY: number; maxX: number; maxY: number };
  
  if (projection && projection.type === 'lcc') {
    // Use dynamic projection parameters from API
    const centralMeridian = projection.centralMeridian ?? -100;
    const originLatitude = projection.originLatitude ?? 38;
    const standardParallel1 = projection.standardParallel1 ?? 33;
    const standardParallel2 = projection.standardParallel2 ?? 45;
    const falseEasting = projection.falseEasting ?? 0;
    const falseNorthing = projection.falseNorthing ?? 0;
    
    lccProjection = `+proj=lcc +lat_0=${originLatitude} +lon_0=${centralMeridian} +lat_1=${standardParallel1} +lat_2=${standardParallel2} +x_0=${falseEasting} +y_0=${falseNorthing} +datum=WGS84 +units=m +no_defs`;
    
    // Use projected bounds from projection parameters or metadata
    if (projection.projectedBounds) {
      projectedBounds = projection.projectedBounds;
    } else if (metadata.projectedBounds) {
      projectedBounds = metadata.projectedBounds;
    } else {
      throw new GeoTIFFParsingError('Projected bounds not available in projection parameters or metadata');
    }
  } else {
    // Fallback to hardcoded US VFR Wall Planning Chart projection
    lccProjection = '+proj=lcc +lat_0=38 +lon_0=-100 +lat_1=33 +lat_2=45 +x_0=0 +y_0=0 +datum=WGS84 +units=m +no_defs';
    
    // Use hardcoded projected bounds or metadata projected bounds
    if (metadata.projectedBounds) {
      projectedBounds = metadata.projectedBounds;
    } else {
      // Use hardcoded projected bounds for US VFR Wall Planning Chart
      projectedBounds = {
        minX: -2189360.538664901,
        minY: -1471397.7140032456,
        maxX: 2668835.6277457676,
        maxY: 1531470.0888555103
      };
    }
  }
  
  const wgs84 = 'EPSG:4326';
  
  return (coord: PixelCoord): GeoCoord => {
    // Convert pixel coordinates to normalized coordinates [0, 1]
    const normalizedX = coord.x / width;
    const normalizedY = coord.y / height;
    
    // Convert normalized coordinates to projected coordinates (meters)
    const projectedX = projectedBounds.minX + normalizedX * (projectedBounds.maxX - projectedBounds.minX);
    const projectedY = projectedBounds.maxY - normalizedY * (projectedBounds.maxY - projectedBounds.minY);
    
    // Transform from Lambert Conformal Conic (projected meters) to WGS84 (lat/long)
    const [longitude, latitude] = proj4(lccProjection, wgs84, [projectedX, projectedY]);
    
    return { latitude, longitude };
  };
}

/**
 * Calculate grid convergence angle for Lambert Conformal Conic projection
 * 
 * Grid convergence is the angle between true north (geographic meridian) and grid north
 * (the vertical lines on the projected map). This angle varies by longitude.
 * 
 * For Lambert Conformal Conic projection:
 * γ = (λ - λ₀) × sin(φ₀)
 * 
 * Where:
 * - γ = grid convergence angle (in degrees)
 * - λ = longitude of the point (in degrees)
 * - λ₀ = central meridian (in degrees)
 * - φ₀ = latitude of projection origin (in degrees)
 * 
 * @param longitude - Longitude of the point in degrees
 * @param centralMeridian - Central meridian of the projection (default: -100 for US VFR Wall Planning Chart)
 * @param projectionOriginLat - Latitude of projection origin (default: 38 for US VFR Wall Planning Chart)
 * @returns Grid convergence angle in degrees (positive = clockwise rotation needed)
 */
export function calculateGridConvergence(
  longitude: number,
  centralMeridian: number = -100,
  projectionOriginLat: number = 38
): number {
  // Convert projection origin latitude to radians for sin calculation
  const phi0Radians = (projectionOriginLat * Math.PI) / 180;
  
  // Calculate grid convergence: γ = (λ - λ₀) × sin(φ₀)
  const convergence = (longitude - centralMeridian) * Math.sin(phi0Radians);
  
  return convergence;
}

/**
 * Get the overview image data as an ImageData object for canvas rendering
 * 
 * @param image - GeoTIFF image
 * @returns Promise resolving to ImageData
 * @throws GeoTIFFParsingError if image data extraction fails
 */
export async function getImageData(image: GeoTIFFImage): Promise<ImageData> {
  try {
    const width = image.getWidth();
    const height = image.getHeight();
    
    // Read raster data
    const rasters = await image.readRasters();
    
    // Create ImageData
    const imageData = new ImageData(width, height);
    const data = imageData.data;
    
    // Convert raster data to RGBA
    // Assuming RGB or RGBA format
    const numBands = rasters.length;
    
    for (let i = 0; i < width * height; i++) {
      if (numBands >= 3) {
        // RGB or RGBA
        data[i * 4] = rasters[0][i]; // R
        data[i * 4 + 1] = rasters[1][i]; // G
        data[i * 4 + 2] = rasters[2][i]; // B
        data[i * 4 + 3] = numBands >= 4 ? rasters[3][i] : 255; // A
      } else if (numBands === 1) {
        // Grayscale
        const value = rasters[0][i];
        data[i * 4] = value;
        data[i * 4 + 1] = value;
        data[i * 4 + 2] = value;
        data[i * 4 + 3] = 255;
      }
    }
    
    return imageData;
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Unknown error';
    throw new GeoTIFFParsingError(
      `Failed to extract image data: ${message}`,
      error instanceof Error ? error : undefined
    );
  }
}

/**
 * Check if a geographic coordinate is within the bounds of a GeoTIFF
 * 
 * @param coord - Geographic coordinate
 * @param bounds - Geographic bounds
 * @returns true if coordinate is within bounds
 */
export function isWithinBounds(coord: GeoCoord, bounds: GeoBounds): boolean {
  return (
    coord.latitude >= bounds.south &&
    coord.latitude <= bounds.north &&
    coord.longitude >= bounds.west &&
    coord.longitude <= bounds.east
  );
}

/**
 * Get user-friendly error message for GeoTIFF parsing errors
 * 
 * @param error - Error object
 * @returns User-friendly error message
 */
export function getGeoTIFFErrorMessage(error: unknown): string {
  if (error instanceof GeoTIFFParsingError) {
    return error.message;
  }
  
  if (error instanceof Error) {
    // Check for LZW decompression errors
    if (error.message.includes('ran off the end of the buffer') || 
        error.message.includes('EOI_CODE') || 
        error.message.includes('LZW')) {
      return 'This base map file is too large for browser display and uses compression that cannot be reliably processed. Please contact support to request a web-optimized overview image.';
    }
    if (error.message.includes('fetch')) {
      return 'Failed to download the base map. Please check your internet connection and try again.';
    }
    if (error.message.includes('CORS')) {
      return 'Unable to access the base map due to security restrictions. Please contact support.';
    }
    if (error.message.includes('parse') || error.message.includes('invalid')) {
      return 'The base map file is corrupted or invalid. Please try a different base map or contact support.';
    }
  }
  
  return 'An unexpected error occurred while loading the base map. Please try again or select a different base map.';
}
